// Wrapper IndexedDB pour le cache des médias
import type { MediaCacheConfig } from '../Config/config';

export interface CachedMedia {
    file_id: string;
    blob: Blob;
    content_type: string;
    cached_at: Date;
    last_used: Date;
    size: number;
    instances: string[]; // IDs des instances utilisant ce média
}

export interface MediaIndex {
    file_id: string;
    instance_id: string;
    component_id: string; // Ex: "ImageModule-1761254129235"
}

export interface CacheStats {
    totalFiles: number;
    totalSize: number; // en bytes
    oldestFile: Date | null;
    newestFile: Date | null;
}

class MediaCacheDBClass {
    private dbName = 'MediaCacheDB';
    private dbVersion = 1;
    private db: IDBDatabase | null = null;
    private config: MediaCacheConfig | null = null;

    /**
     * Initialise la base de données IndexedDB
     */
    async init(config: MediaCacheConfig): Promise<void> {
        this.config = config;
        
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(this.dbName, this.dbVersion);

            request.onerror = () => {
                console.error('❌ Erreur ouverture IndexedDB:', request.error);
                reject(request.error);
            };

            request.onsuccess = () => {
                this.db = request.result;
                this.log('✅ IndexedDB initialisé');
                resolve();
            };

            request.onupgradeneeded = (event) => {
                const db = (event.target as IDBOpenDBRequest).result;
                
                // Store pour les fichiers médias
                if (!db.objectStoreNames.contains('media_files')) {
                    const mediaStore = db.createObjectStore('media_files', { keyPath: 'file_id' });
                    mediaStore.createIndex('cached_at', 'cached_at', { unique: false });
                    mediaStore.createIndex('last_used', 'last_used', { unique: false });
                    mediaStore.createIndex('size', 'size', { unique: false });
                }

                // Store pour l'index des instances
                if (!db.objectStoreNames.contains('media_index')) {
                    const indexStore = db.createObjectStore('media_index', { keyPath: 'id', autoIncrement: true });
                    indexStore.createIndex('file_id', 'file_id', { unique: false });
                    indexStore.createIndex('instance_id', 'instance_id', { unique: false });
                    indexStore.createIndex('component_id', 'component_id', { unique: false });
                }

                this.log('🔄 Schéma IndexedDB mis à jour');
            };
        });
    }

    /**
     * Vérifie si un média est en cache
     */
    async hasMedia(fileId: string): Promise<boolean> {
        if (!this.db) return false;

        return new Promise((resolve) => {
            const transaction = this.db!.transaction(['media_files'], 'readonly');
            const store = transaction.objectStore('media_files');
            const request = store.get(fileId);

            request.onsuccess = () => {
                resolve(request.result !== undefined);
            };

            request.onerror = () => {
                this.log('❌ Erreur vérification cache:', request.error);
                resolve(false);
            };
        });
    }

    /**
     * Récupère un média depuis le cache
     */
    async getMedia(fileId: string): Promise<CachedMedia | null> {
        if (!this.db) return null;

        return new Promise((resolve) => {
            const transaction = this.db!.transaction(['media_files'], 'readwrite');
            const store = transaction.objectStore('media_files');
            const request = store.get(fileId);

            request.onsuccess = () => {
                const media = request.result;
                if (media) {
                    // Mettre à jour last_used
                    media.last_used = new Date();
                    const updateRequest = store.put(media);
                    
                    updateRequest.onsuccess = () => {
                        this.log(`✅ Cache HIT: ${fileId}`);
                        resolve(media);
                    };
                    
                    updateRequest.onerror = () => {
                        this.log('❌ Erreur mise à jour last_used:', updateRequest.error);
                        resolve(media); // Retourner quand même le média
                    };
                } else {
                    this.log(`⬇️ Cache MISS: ${fileId}`);
                    resolve(null);
                }
            };

            request.onerror = () => {
                this.log('❌ Erreur récupération cache:', request.error);
                resolve(null);
            };
        });
    }

    /**
     * Sauvegarde un média dans le cache
     */
    async saveMedia(
        fileId: string, 
        blob: Blob, 
        contentType: string, 
        instanceId: string, 
        componentId: string
    ): Promise<void> {
        if (!this.db) return;

        const cachedMedia: CachedMedia = {
            file_id: fileId,
            blob,
            content_type: contentType,
            cached_at: new Date(),
            last_used: new Date(),
            size: blob.size,
            instances: [instanceId]
        };

        return new Promise((resolve, reject) => {
            const transaction = this.db!.transaction(['media_files', 'media_index'], 'readwrite');
            
            // Sauvegarder le média
            const mediaStore = transaction.objectStore('media_files');
            const mediaRequest = mediaStore.put(cachedMedia);

            // Sauvegarder l'index
            const indexStore = transaction.objectStore('media_index');
            const indexEntry: MediaIndex = {
                file_id: fileId,
                instance_id: instanceId,
                component_id: componentId
            };
            const indexRequest = indexStore.add(indexEntry);

            transaction.oncomplete = () => {
                this.log(`💾 Cache SAVE: ${fileId} (${this.formatSize(blob.size)})`);
                resolve();
            };

            transaction.onerror = () => {
                this.log('❌ Erreur sauvegarde cache:', transaction.error);
                reject(transaction.error);
            };
        });
    }

    /**
     * Supprime un média du cache
     */
    async deleteMedia(fileId: string): Promise<void> {
        if (!this.db) return;

        return new Promise((resolve, reject) => {
            const transaction = this.db!.transaction(['media_files', 'media_index'], 'readwrite');
            
            // Supprimer le média
            const mediaStore = transaction.objectStore('media_files');
            const mediaRequest = mediaStore.delete(fileId);

            // Supprimer tous les index liés
            const indexStore = transaction.objectStore('media_index');
            const indexRequest = indexStore.index('file_id').openCursor(IDBKeyRange.only(fileId));

            indexRequest.onsuccess = (event) => {
                const cursor = (event.target as IDBRequest).result;
                if (cursor) {
                    cursor.delete();
                    cursor.continue();
                }
            };

            transaction.oncomplete = () => {
                this.log(`🗑️ Cache DELETE: ${fileId}`);
                resolve();
            };

            transaction.onerror = () => {
                this.log('❌ Erreur suppression cache:', transaction.error);
                reject(transaction.error);
            };
        });
    }

    /**
     * Met à jour les instances utilisant un média
     */
    async updateMediaInstances(fileId: string, instances: string[]): Promise<void> {
        if (!this.db) return;

        return new Promise((resolve, reject) => {
            const transaction = this.db!.transaction(['media_files'], 'readwrite');
            const store = transaction.objectStore('media_files');
            const request = store.get(fileId);

            request.onsuccess = () => {
                const media = request.result;
                if (media) {
                    media.instances = instances;
                    media.last_used = new Date();
                    
                    const updateRequest = store.put(media);
                    updateRequest.onsuccess = () => resolve();
                    updateRequest.onerror = () => reject(updateRequest.error);
                } else {
                    resolve();
                }
            };

            request.onerror = () => reject(request.error);
        });
    }

    /**
     * Récupère les statistiques du cache
     */
    async getStats(): Promise<CacheStats> {
        if (!this.db) {
            return { totalFiles: 0, totalSize: 0, oldestFile: null, newestFile: null };
        }

        return new Promise((resolve) => {
            const transaction = this.db!.transaction(['media_files'], 'readonly');
            const store = transaction.objectStore('media_files');
            const request = store.getAll();

            request.onsuccess = () => {
                const files = request.result as CachedMedia[];
                
                const stats: CacheStats = {
                    totalFiles: files.length,
                    totalSize: files.reduce((sum, file) => sum + file.size, 0),
                    oldestFile: files.length > 0 ? new Date(Math.min(...files.map(f => f.cached_at.getTime()))) : null,
                    newestFile: files.length > 0 ? new Date(Math.max(...files.map(f => f.cached_at.getTime()))) : null
                };

                this.log(`📊 Cache STATS: ${stats.totalFiles} fichiers, ${this.formatSize(stats.totalSize)}`);
                resolve(stats);
            };

            request.onerror = () => {
                this.log('❌ Erreur récupération stats:', request.error);
                resolve({ totalFiles: 0, totalSize: 0, oldestFile: null, newestFile: null });
            };
        });
    }

    /**
     * Nettoie les médias orphelins (non utilisés par aucune instance)
     */
    async cleanupOrphanedMedia(): Promise<number> {
        if (!this.db) return 0;

        return new Promise((resolve) => {
            const transaction = this.db!.transaction(['media_files', 'media_index'], 'readwrite');
            const mediaStore = transaction.objectStore('media_files');
            const indexStore = transaction.objectStore('media_index');
            
            let deletedCount = 0;
            const mediaRequest = mediaStore.openCursor();

            mediaRequest.onsuccess = (event) => {
                const cursor = (event.target as IDBRequest).result;
                if (cursor) {
                    const media = cursor.value as CachedMedia;
                    
                    // Vérifier si le média est utilisé par au moins une instance
                    const indexRequest = indexStore.index('file_id').count(IDBKeyRange.only(media.file_id));
                    
                    indexRequest.onsuccess = () => {
                        if (indexRequest.result === 0) {
                            // Média orphelin, le supprimer
                            cursor.delete();
                            deletedCount++;
                            this.log(`🗑️ Suppression média orphelin: ${media.file_id}`);
                        }
                        cursor.continue();
                    };
                } else {
                    this.log(`🧹 Nettoyage terminé: ${deletedCount} médias supprimés`);
                    resolve(deletedCount);
                }
            };

            mediaRequest.onerror = () => {
                this.log('❌ Erreur nettoyage:', mediaRequest.error);
                resolve(0);
            };
        });
    }

    /**
     * Ferme la connexion à la base de données
     */
    close(): void {
        if (this.db) {
            this.db.close();
            this.db = null;
            this.log('🔒 IndexedDB fermé');
        }
    }

    /**
     * Log conditionnel selon la configuration
     */
    private log(message: string, ...args: any[]): void {
        if (this.config?.enableLogs) {
            console.log(`[MediaCacheDB] ${message}`, ...args);
        }
    }

    /**
     * Formate une taille en bytes en format lisible
     */
    private formatSize(bytes: number): string {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }
}

// Export singleton
export const MediaCacheDB = new MediaCacheDBClass();

